import React, { useRef, useEffect } from 'react';
import { gsap } from '../../lib/gsapSetup';
import { useTranslation } from '../../i18n/LanguageContext';
import step01 from '../../assets/images/steps/step-01-pick-service.webp';
import step02 from '../../assets/images/steps/step-02-describe-goods.webp';
import step03 from '../../assets/images/steps/step-03-set-addresses.webp';
import step04 from '../../assets/images/steps/step-04-choose-vehicle.webp';
import step05 from '../../assets/images/steps/step-05-select-drivers.webp';
import step06 from '../../assets/images/steps/step-06-await-confirmation.webp';
import step07 from '../../assets/images/steps/step-07-coordinate-app.webp';
import step08 from '../../assets/images/steps/step-08-realtime-tracking.webp';
import step09 from '../../assets/images/steps/step-09-rate-driver.webp';

const STEPS = [
  {
    number: '01',
    titleKey: 'how.0.title',
    descKey: 'how.0.desc',
    image: step01,
    alt: 'Sélection du service sur smartphone',
  },
  {
    number: '02',
    titleKey: 'how.1.title',
    descKey: 'how.1.desc',
    image: step02,
    alt: 'Marchandises et cartons prêts au transport',
  },
  {
    number: '03',
    titleKey: 'how.2.title',
    descKey: 'how.2.desc',
    image: step03,
    alt: 'Carte GPS avec adresses de collecte et livraison',
  },
  {
    number: '04',
    titleKey: 'how.3.title',
    descKey: 'how.3.desc',
    image: step04,
    alt: 'Différents types de véhicules de transport',
  },
  {
    number: '05',
    titleKey: 'how.4.title',
    descKey: 'how.4.desc',
    image: step05,
    alt: 'Liste de transporteurs disponibles',
  },
  {
    number: '06',
    titleKey: 'how.5.title',
    descKey: 'how.5.desc',
    image: step06,
    alt: 'Transporteur confirmant une mission',
  },
  {
    number: '07',
    titleKey: 'how.6.title',
    descKey: 'how.6.desc',
    image: step07,
    alt: 'Communication par chat et appel vocal',
  },
  {
    number: '08',
    titleKey: 'how.7.title',
    descKey: 'how.7.desc',
    image: step08,
    alt: 'Suivi de commande et notification en temps réel',
  },
  {
    number: '09',
    titleKey: 'how.8.title',
    descKey: 'how.8.desc',
    image: step09,
    alt: 'Évaluation et notation du transporteur',
  },
];

/**
 * HOW IT WORKS — Pinned section with stacking cards.
 *
 * Inspired by GSAP pinned-panels-with-overscroll demo.
 * Each step is a full-height panel that reveals on scroll.
 * Image on one side, text on the other, alternating.
 * Progress line draws as you scroll through.
 */
export default function HowItWorks() {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const lineRef = useRef(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Heading reveal animation
      if (headingRef.current) {
        gsap.fromTo(
          headingRef.current,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: { trigger: headingRef.current, start: 'top 80%' },
          },
        );
      }

      // Progress line draw
      if (lineRef.current) {
        gsap.fromTo(
          lineRef.current,
          { scaleY: 0 },
          {
            scaleY: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 20%',
              end: 'bottom 80%',
              scrub: true,
            },
          },
        );
      }

      // Each step card animates in
      const cards = sectionRef.current.querySelectorAll('.step-card');
      cards.forEach((card, i) => {
        const img = card.querySelector('.step-img');
        const text = card.querySelector('.step-text');
        const num = card.querySelector('.step-num');

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: card,
            start: 'top 75%',
            end: 'top 30%',
            scrub: 1,
          },
        });

        // Image parallax reveal
        if (img) {
          tl.fromTo(
            img,
            { opacity: 0, scale: 1.2, y: 60 },
            { opacity: 1, scale: 1, y: 0, duration: 1 },
            0,
          );
        }

        // Text slide in from opposite side
        if (text) {
          const xDir = i % 2 === 0 ? 60 : -60;
          tl.fromTo(
            text,
            { opacity: 0, x: xDir },
            { opacity: 1, x: 0, duration: 1 },
            0.1,
          );
        }

        // Number scale
        if (num) {
          tl.fromTo(
            num,
            { opacity: 0, scale: 0.5 },
            { opacity: 0.1, scale: 1, duration: 0.8 },
            0,
          );
        }

        // Image parallax on continued scroll
        if (img) {
          gsap.to(img, {
            yPercent: -20,
            ease: 'none',
            scrollTrigger: {
              trigger: card,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          });
        }
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id='how-it-works'
      className='relative py-32 lg:py-40 bg-linx-surface-2'
    >
      {/* Header */}
      <div className='section-container mb-24 lg:mb-32'>
        <span className='inline-block font-mono text-xs text-secondary-dark tracking-[0.2em] uppercase mb-4 opacity-80'>
          {t('how.label')}
        </span>
        <h2
          ref={headingRef}
          className='font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.05]'
          aria-label={t('how.heading')}
        >
          {t('how.heading')}
        </h2>
      </div>

      {/* Progress line (vertical center) */}
      <div className='hidden lg:block absolute left-1/2 top-[300px] bottom-[200px] w-[1px] -translate-x-1/2 z-0'>
        <div className='w-full h-full bg-primary/[0.06]' />
        <div
          ref={lineRef}
          className='absolute top-0 left-0 w-full h-full bg-gradient-to-b from-secondary to-secondary-dark origin-top'
          style={{ transform: 'scaleY(0)' }}
        />
      </div>

      {/* Steps */}
      <div className='section-container relative z-10 space-y-24 lg:space-y-40'>
        {STEPS.map((step, i) => {
          const isEven = i % 2 === 0;
          return (
            <div
              key={step.number}
              className='step-card grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center'
            >
              {/* Image */}
              <div
                className={`overflow-hidden rounded-3xl ${isEven ? '' : 'lg:order-2'}`}
              >
                <img
                  src={step.image}
                  alt={step.alt}
                  className='step-img w-full h-[400px] lg:h-[500px] object-cover rounded-3xl will-change-transform'
                  loading='lazy'
                />
              </div>

              {/* Text */}
              <div
                className={`step-text relative ${isEven ? '' : 'lg:order-1'}`}
              >
                {/* Big background number */}
                <span className='step-num absolute -top-8 font-display text-[8rem] sm:text-[10rem] font-bold text-primary opacity-[0.04] leading-none pointer-events-none select-none'>
                  {step.number}
                </span>

                <span className='inline-block font-mono text-xs text-secondary-dark tracking-[0.2em] uppercase mb-4'>
                  {t('how.step')} {step.number}
                </span>
                <h3 className='font-display text-3xl sm:text-4xl font-bold text-primary mb-5'>
                  {t(step.titleKey)}
                </h3>
                <p className='text-lg text-linx_text-secondary leading-relaxed max-w-md'>
                  {t(step.descKey)}
                </p>

                {/* Dot indicator on the center line */}
                <div
                  className={`hidden lg:block absolute top-1/2 ${isEven ? '-right-[calc(50vw-50%+8px)]' : '-left-[calc(50vw-50%+8px)]'} w-4 h-4 rounded-full bg-secondary border-4 border-linx-surface-2 z-20`}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
